import { ReserveFormProvider } from "@/components/reserve/Reserve/ReserveFormProvider";
import { ReserveOptions } from "@/components/reserve/Reserve/types";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import { IResource, ResourceType } from "@gettactic/api";
import {
  UseDisclosureReturn,
  getDefaultsHoursFromOffice
} from "@gettactic/helpers/src";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import { useGlobalEscapeKey } from "@gettactic/helpers/src/hooks/useGlobalEscapeKey";
import { calculateReservationDefaults } from "@gettactic/helpers/src/schedules/calculateReservationDefaults";
import { useMapSchedulable } from "@gettactic/hooks";
import { useRoomReservationWeb } from "@gettactic/hooks";
import { useMap } from "@webV2/map/MapProvider";
import { useReserve } from "@webV2/providers/ReserveProvider/context";
import { addMinutes } from "date-fns";
import { MapDailyViewCard } from "@webV2/components/cards/MapDailyCalendarViewCard/MapDailyViewCard";
import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";
import { MapReserveCardContent } from "./MapReserveCardContent";
export interface MapReserveCardProps
  extends Omit<ReserveOptions, "resource" | "resourceType"> {
  resourceType?: ResourceType;
  resource?: IResource | null;
  onCancel?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  dayViewDisclosure?: UseDisclosureReturn;
  requireDelegatedPermission?: boolean;
  msAuthUrl?: string;
  isCompact?: boolean;
  isModal?: boolean;
  onSuccess?: () => void;
  isOnReservationModal?: boolean;
  reservationCardIsOpen?: boolean;
}

export const MapReserveCard: React.FC<MapReserveCardProps> = (props) => {
  const {
    reserveData: reserveProps,
    resource: reloadedResource,
    closeReserveModal,
    isReserveOpen,
    openReserveModal
  } = useReserve();

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");
  const wpRequestsRef = React.useRef<WorkplaceRequestsRef | null>(null);
  const isFull = isMd && !props.isCompact;

  const resource = reloadedResource || reserveProps?.resource;

  const isOpen = isReserveOpen && !!reserveProps && !!resource;

  // Track current form time/duration to preserve when switching resources
  const currentFormTimeRef = React.useRef<{
    time: Date;
    duration: number;
  } | null>(null);

  // Callback to update ref when form time changes
  const handleFormTimeChange = React.useCallback(
    (time: Date) => {
      if (currentFormTimeRef.current) {
        currentFormTimeRef.current.time = time;
      } else {
        currentFormTimeRef.current = {
          time,
          duration: reserveProps?.duration || 60
        };
      }
    },
    [reserveProps?.duration]
  );

  // Callback to update ref when form duration changes
  const handleFormDurationChange = React.useCallback(
    (duration: number) => {
      if (currentFormTimeRef.current) {
        currentFormTimeRef.current.duration = duration;
      } else {
        currentFormTimeRef.current = {
          time: reserveProps?.time || new Date(),
          duration
        };
      }
    },
    [reserveProps?.time]
  );

  // Initialize ref when reserveProps are set initially
  React.useEffect(() => {
    if (
      reserveProps?.time &&
      reserveProps?.duration &&
      !currentFormTimeRef.current
    ) {
      currentFormTimeRef.current = {
        time: reserveProps.time,
        duration: reserveProps.duration
      };
    }
  }, [reserveProps?.time, reserveProps?.duration]);

  const resetToInitialTime = () => {
    const initialTime = closeReserveModal();
    if (initialTime) {
      // they only get updated together for now
      if (initialTime.mapStart || initialTime.mapEnd) {
        changeStartDate(initialTime.mapStart || null);
        changeEndTime(initialTime.mapEnd || null);
      } else {
        changeStartDate(initialTime.start || null);
        changeEndTime(
          addMinutes(
            initialTime.start || new Date(),
            initialTime.duration || 0
          ) || null
        );
      }
    }
  };

  const onClose = () => {
    // User clicked back button or escape - reset to initial time and close
    events.trigger("onFocusResource", null);
    resetToInitialTime();
    currentFormTimeRef.current = null;
  };

  useGlobalEscapeKey(onClose);

  const { isVisible } = useCloseAnimationTimeout(isOpen);
  const {
    requireDelegatedPermission,
    msAuthUrl,
    onRoomChange,
    hasMSCalendar,
    hasGoogleCalendar
  } = useRoomReservationWeb({
    isOpen: isVisible,
    isRoomReservation: resource?.type === "meeting_room"
  });

  const {
    events,
    startDate,
    endDate,
    floor,
    currentOffice,
    timeMode,
    mapInstanceId,
    changeStartDate,
    changeEndTime
  } = useMap();

  const response = useMapSchedulable({
    officeId: currentOffice.id,
    parentId: floor?.id ?? null,
    startDate: startDate?.toISOString() ?? "",
    endDate: endDate?.toISOString() ?? "",
    duration: null,
    hierarchy: false,
    coreApi: true,

    // it's available from the new map
    enabled:
      !!startDate &&
      !!endDate &&
      !!floor &&
      !!currentOffice.id &&
      !!resource?.id
  });

  const haveAvailableSlots = useMemo(() => {
    return (
      response.schedulable &&
      response.schedulable.length > 0 &&
      (response.schedulable.find((r) => r.id === resource?.id)
        ?.available_slots ?? 0) > 0
    );
  }, [response?.schedulable, resource?.id]);

  React.useEffect(() => {
    const onFocusResource = (resource: IResource | null) => {
      if (!resource) {
        closeReserveModal();
        // Don't reset time here - time reset is handled by:
        // 1. onClose (back button/escape) or
        // 2. onSuccess (form submit)
        // Not resetting here prevents losing time changes when switching resources
        props.dayViewDisclosure?.onOpenChange(false);
        currentFormTimeRef.current = null;
        return;
      }

      // Determine which time to use
      let _startDate: Date | undefined;
      let _duration: number;

      // When switching resources and modal is already open, preserve current time
      if (isReserveOpen && currentFormTimeRef.current) {
        // Use the tracked form time which includes user's changes
        _startDate = currentFormTimeRef.current.time;
        _duration = currentFormTimeRef.current.duration;
      } else if (isReserveOpen && reserveProps?.time) {
        // Editing existing reservation: use existing reservation times
        _startDate = reserveProps.time;
        _duration = reserveProps.duration || 60;
      } else {
        // For new reservations: calculate defaults from map filter and office settings
        const defaultHours = getDefaultsHoursFromOffice(currentOffice);
        const defaults = calculateReservationDefaults({
          timeMode,
          mapStartDate: startDate,
          mapEndDate: endDate,
          resourceType: resource.type,
          defaultHours
        });
        _startDate = defaults.startDate;
        _duration = defaults.duration;
      }

      openReserveModal({
        start: _startDate || undefined,
        time: _startDate,
        resource: resource,
        duration: _duration,
        floorId: floor?.id ?? null,
        resourceType: resource.type,
        office: currentOffice,
        mapStart: startDate,
        mapEnd: endDate
      });
    };
    events.register("onFocusResource", onFocusResource);

    return () => {
      events.unregister("onFocusResource", onFocusResource);
    };
  }, [
    startDate,
    endDate,
    floor,
    currentOffice,
    isReserveOpen,
    timeMode,
    reserveProps
  ]);

  return (
    <ReserveFormProvider
      key={props.isOnReservationModal ? undefined : resource?.id}
      onStartTimeChange={handleFormTimeChange}
      onDurationChange={handleFormDurationChange}
      onSuccess={() => {
        // Reset to initial time on successful submit
        resetToInitialTime();
        currentFormTimeRef.current = null;
        props.onSuccess?.();
        toast.success("Reservation created successfully");
      }}
      onRoomChange={onRoomChange}
      hasMSCalendar={hasMSCalendar}
      hasGoogleCalendar={hasGoogleCalendar}
      wpRequestsRef={wpRequestsRef}
      requireDelegatedPermission
      {...reserveProps}
      resource={resource}
      isOnReservationModal={props.isOnReservationModal}
      isReservationCardIsOpen={isVisible}
    >
      {props.dayViewDisclosure &&
        createPortal(
          <MapDailyViewCard
            isOpen={props.dayViewDisclosure.isOpen}
            onOpenChange={props.dayViewDisclosure.onOpenChange}
            office={currentOffice}
            isModal={props.isModal}
            isReserving={isReserveOpen}
          />,
          window.document.querySelector(
            `#map-daily-view-card-holder-${mapInstanceId.replace(/:/g, "")}`
          ) || document.body
        )}
      <MapReserveCardContent
        {...props}
        resource={resource!}
        haveAvailableSlots={haveAvailableSlots}
        onClose={onClose}
        isMd={isMd}
        isFull={isFull}
        isOpen={isOpen}
        msAuthUrl={msAuthUrl}
        requireDelegatedPermission={requireDelegatedPermission}
        isVisible={isVisible}
      />
    </ReserveFormProvider>
  );
};
